import { useMemo, useRef, useState } from 'react'
import { useBilling } from '../contexts/BillingContext'
import { useData } from '../store/DataContext'
import { BillLine, BillingAccount, BillingConfig, EditorPrefill, PendingCharge, SalesDocType } from '../types/billing'
import { addDays, calculateLine, documentTotals, lineFromItem, makeId, newClientKey, taxFields, taxFor, todayLusaka } from '../utils/billingMoney'

export function chargeToLine(config: BillingConfig, charge: PendingCharge): BillLine {
  const extra = { sourceRef: charge.sourceRef, sourceLabel: `${charge.kind} · ${charge.encounterLabel}`, quantity: charge.quantity }
  if (charge.item) return lineFromItem(config, charge.item, { ...extra, description: charge.description === charge.item.name ? charge.item.name : `${charge.item.name} — ${charge.description}` })
  const tax = taxFor(config)
  return calculateLine({
    id: makeId('ln'), description: charge.description, quantity: charge.quantity, unitPrice: charge.unitPrice ?? 0, discountPct: 0,
    ...taxFields(tax), taxInclusive: config.taxes.pricesIncludeTax, ...extra,
  })
}

/** State and actions for the invoice / quotation / order editor. */
export function useSalesDocumentEditor(type: SalesDocType, id: string | undefined, prefill: EditorPrefill | undefined) {
  const { config, documents, accounts, pendingCharges, saveDocument, can } = useBilling()
  const { encounters } = useData()
  const existing = id ? documents.find((d) => d.id === id) : undefined
  const clientKey = useRef(newClientKey())
  const docId = id ?? `${type}-${clientKey.current}`
  const today = todayLusaka()

  const initialAccount = existing ? accounts.find((a) => a.id === existing.accountId) ?? null : prefill?.accountId ? accounts.find((a) => a.id === prefill.accountId) ?? null : null
  const [account, setAccountState] = useState<BillingAccount | null>(initialAccount)
  const [patient, setPatient] = useState<BillingAccount | null>(
    existing?.patientId && existing.accountType !== 'Patient' ? accounts.find((a) => a.patientId === existing.patientId) ?? null : null,
  )
  const [encounterId, setEncounterId] = useState(existing?.encounterId ?? prefill?.charges?.[0]?.encounterId ?? '')
  const [date, setDate] = useState(existing?.date ?? today)
  const [dueDate, setDueDate] = useState(existing?.dueDate ?? addDays(today, config.invoiceDefaults.termsDays))
  const [validUntil, setValidUntil] = useState(existing?.validUntil ?? addDays(today, config.invoiceDefaults.quotationValidityDays))
  const [paymentTerms, setPaymentTerms] = useState(existing?.paymentTerms ?? config.invoiceDefaults.paymentTerms)
  const [notes, setNotes] = useState(existing?.notes ?? config.invoiceDefaults.notes)
  const [footer, setFooter] = useState(existing?.footer ?? config.invoiceDefaults.footer)
  const [lines, setLines] = useState<BillLine[]>(existing?.lines ?? (prefill?.charges ?? []).map((c) => chargeToLine(config, c)))
  const [split, setSplit] = useState<{ enabled: boolean; payer: BillingAccount | null; percent: number }>({ enabled: false, payer: null, percent: 80 })
  const [saving, setSaving] = useState<'draft' | 'issue' | null>(null)
  const [error, setError] = useState('')
  const [dirty, setDirty] = useState(false)

  const touch = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setDirty(true) }
  const setAccount = (a: BillingAccount | null) => {
    setAccountState(a)
    setDirty(true)
    if (a?.type === 'Patient') setPatient(null)
    if (!existing) setEncounterId('')
  }

  const patientId = account?.type === 'Patient' ? account.patientId : patient?.patientId
  const patientEncounters = useMemo(
    () => (patientId ? encounters.filter((e) => e.patientId === patientId).sort((a, b) => b.startedAt.localeCompare(a.startedAt)) : []),
    [encounters, patientId],
  )
  const usedRefs = new Set(lines.map((l) => l.sourceRef).filter(Boolean))
  const availableCharges = useMemo(
    () => (type === 'invoice' && patientId ? pendingCharges.filter((c) => c.patientId === patientId && !usedRefs.has(c.sourceRef) && (!encounterId || c.encounterId === encounterId)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingCharges, patientId, encounterId, lines, type],
  )
  const addCharges = (charges: PendingCharge[]) => {
    setLines((prev) => [...prev, ...charges.map((c) => chargeToLine(config, c))])
    setDirty(true)
  }

  const totals = documentTotals(lines)

  const save = async (issue: boolean) => {
    setError('')
    if (!account) return setError('Choose the patient or payer to bill.')
    if (split.enabled && issue && !split.payer) return setError('Choose the payer who covers part of this invoice.')
    if (issue && lines.some((l) => !l.fixed && l.unitPrice <= 0)) return setError('Every line needs a price before the invoice can be issued. Map unpriced charges to an item in Items & Services.')
    setSaving(issue ? 'issue' : 'draft')
    const encounter = patientEncounters.find((e) => e.id === encounterId)
    const result = await saveDocument(
      {
        id: docId,
        type,
        account,
        patient: patient?.patientId ? { id: patient.patientId, name: patient.name, bmcFileNumber: patient.bmcFileNumber ?? '' } : undefined,
        encounterId: encounter?.id,
        encounterLabel: encounter ? `Visit ${encounter.startedAt.slice(0, 10)}${encounter.consultationType ? ` · ${encounter.consultationType} consultation` : ''}` : undefined,
        date,
        dueDate: type === 'invoice' ? dueDate : undefined,
        validUntil: type === 'quotation' ? validUntil : undefined,
        paymentTerms,
        notes,
        footer,
        lines,
        split: split.enabled && split.payer ? { payer: split.payer, percent: split.percent } : undefined,
      },
      issue,
    )
    setSaving(null)
    if (!result.ok) {
      setError(result.error ?? 'Could not save.')
      return null
    }
    setDirty(false)
    return result
  }

  return {
    config, existing, docId, account, setAccount, patient, setPatient: touch(setPatient), encounterId, setEncounterId: touch(setEncounterId),
    patientEncounters, date, setDate: touch(setDate), dueDate, setDueDate: touch(setDueDate), validUntil, setValidUntil: touch(setValidUntil),
    paymentTerms, setPaymentTerms: touch(setPaymentTerms), notes, setNotes: touch(setNotes), footer, setFooter: touch(setFooter),
    lines, setLines: touch(setLines), split, setSplit: touch(setSplit), availableCharges, addCharges, totals, save, saving, error, dirty, can,
  }
}
